import { autobind } from 'core-decorators'
import { inject, observer } from 'mobx-react'
import * as React from 'react'
import { Button, ButtonBar, NextButton } from '../../../shared/components/Button'
import { ContentContainerBody, ContentRow, FlexItem, SplitContentContainer } from '../../../shared/components/Content'
import { Headlines, InnerContainer, Select, Option } from '../../../shared/components/Elements'
import { Headline, HeadlineCSS, SubHeadline } from '../../../shared/components/Headlines'
import { RadioButtonControl } from '../../../shared/components/RadioButtonControl'
import { SideInformation } from '../../../shared/components/SideInformation'
import { Jaw } from '../../../shared/Constants'
import { breakpoint, styled } from '../../../shared/styled-components'
import { Utils } from '../../../shared/Utils'
import { CalculatorStoreInstance } from '../../../stores/CalculatorStore'
import { SegmentedProgressBar } from './result/SegmentedProgressBar'

interface IProps {
  store?: CalculatorStoreInstance
  history?: History
  jaw: Jaw
}

@inject('store')
@observer
export class InsuranceView extends React.Component<IProps> {
  // -------------------------------------------------------------------------- LIFECYCLE

  componentDidMount () {
    const { store } = this.props
    store!.validateRequirementsGiven()
  }

  // -------------------------------------------------------------------------- EVENTS

  @autobind
  private _onInsuranceTypeChange (insuranceType: 'public'|'private') {
    const { store } = this.props
    store!.offer.setInsuranceType(insuranceType)
  }

  @autobind
  private _onInsuranceBonusChange (e: React.ChangeEvent<HTMLSelectElement>) {
    const { store } = this.props
    store!.offer.setInsuranceBonus(e.target.value as any)
  }

  // -------------------------------------------------------------------------- RENDERING

  render () {
    const { store } = this.props
    const { offer } = store

    return (<InnerContainer padded>
      <Headlines>
        <Headline centered>Versicherung</Headline>
        <SubHeadline centered>Sind Sie Privat- oder Kassenpatient?</SubHeadline>
      </Headlines>

      <SplitContentContainer>
        <ContentContainerBody vertical>
          <ContentRow paddedVertically>
            <FlexItem>
              {offer.insuranceType == 'public'
                ? 'Wir stellen Ihnen diese Frage, da Sie als Kassenpatient einen Festzuschuss bekommen. Ihre Daten werden nicht an Dritte weitergegeben, sondern dienen ausschließlich der exakten Kalkulation Ihrer Implantatkosten.'
                : 'Als Privatpatient kalkuliert der Implantatkostenrechner mit den Gesamtkosten für die implantologische Versorgung. Welchen Anteil der Gesamtkosten Sie erstattet bekommen hängt individuell von Ihrem privaten Versicherer ab.'}
            </FlexItem>
            <TypeSelectionControls>
              <TypeSelectionHeadline>Versicherung</TypeSelectionHeadline>
              <RadioButtons>
                <RadioButton label='Kassenpatient' checked={offer.insuranceType === 'public'} onChange={this._onInsuranceTypeChange.bind(this, 'public')} />
                <RadioButton label='Privatpatient' checked={offer.insuranceType === 'private'} onChange={this._onInsuranceTypeChange.bind(this, 'private')} />
              </RadioButtons>
            </TypeSelectionControls>
          </ContentRow>
          {offer.insuranceType === 'public' && <BonusRow>
            <div>
              <BonusImage src={Utils.getAssetPath('/assets/calculator/bonusheft.png')} />
            </div>
            <BonusRowRight>
              <BonusHeadline>Bitte wählen Sie aus:</BonusHeadline>
              <Select value={offer.insuranceBonus} onChange={this._onInsuranceBonusChange}>
                <Option value='zero_to_four_years'>Bonusheft die letzten 0-4 Jahre in Folge abgestempelt</Option>
                <Option value='five_to_nine_years'>Bonusheft die letzten 5-9 Jahre in Folge abgestempelt</Option>
                <Option value='at_least_ten_years'>Bonusheft mindestens 10 Jahre in Folge abgestempelt</Option>
                <Option value='free'>Ich bin befreit und bekomme den doppelten Festzuschuss</Option>
                <Option value='unknown'>Ich weiß es nicht</Option>
              </Select>
            </BonusRowRight>
          </BonusRow>}
        </ContentContainerBody>
        <SideInformation
          onBackClick={() => store!.goBack()}
          onNextClick={() => store!.goForward()}
          infoText='implacheck® Ihnen eine Zahnzusatzversicherung bietet, die Ihnen bis zu 100% Ihres Eigenanteils erstattet?'
          longInfoText='implacheck® Ihnen eine Zahnzusatzversicherung bietet, die Ihnen bis zu 100% Ihres Eigenanteils erstattet? Für viele Patienten bleibt eine hochwertige Implantatversorgung aufgrund der hohen Kosten unerreichbar. Durch einen der Tarife unserer Zahnzusatzversicherung sorgen Sie frühzeitig vor und freuen sich im Akutfall über einen reduzierten oder im besten Fall keinen Eigenanteil. Unsere implacheck®-Versicherung ist Testsieger (Stiftung Warentest 05/2019) und auch im Akutfall noch abschließbar.' />
      </SplitContentContainer>

      <ButtonBar>
        <Button onClick={() => store!.goBack()}>Zurück</Button>
        <SegmentedProgressBar segmentCount={2} segment={0} />
        <NextButton primary onClick={() => store!.goForward()}>Weiter</NextButton>
      </ButtonBar>
    </InnerContainer>)
  }
}

const TypeSelectionControls = styled(FlexItem)`
  flex: 0;
  margin-bottom: 20px;
  ${breakpoint('tablet')`
    flex: 0 0 230px;
    margin-bottom: 0;
  `}
`

const BonusHeadline = styled.h4`
  ${HeadlineCSS};
  font-size: 20px !important;
  font-weight: 500;
`

const RadioButtons = styled.div`
  display: flex;
  flex-direction: column;
`

const RadioButton = styled(RadioButtonControl)`
  margin-bottom: 10px;  
  &:last-child {
    margin-bottom: 0;
  }
`

const TypeSelectionHeadline = styled.h4`
  ${HeadlineCSS};
  font-weight: 500;
  font-size: 20px !important;
  margin-bottom: 15px;
`

const BonusRow = styled(ContentRow)`
  flex-direction: column;
  padding: 0;
  ${breakpoint('tablet')`
    padding: 20px;
    flex-direction: row;
  `}
`

const BonusImage = styled.img`
  width: 350px;
  margin-right: 20px;
`

const BonusRowRight = styled.div`
  display: flex;
  flex-direction: column;
`
